// Two-field phone input (#7r2 → #intl-auth-1c): country dropdown + phone
// field.
//
// #intl-auth-1c: the dropdown is now a REAL 7-country <select> sourced from
// @jnati/phone's COUNTRIES (IN, US, CA, AU, GB, AE, SG), defaulting to
// India. It's a controlled input — the parent owns `country` state and
// passes it to @jnati/phone (validate + toE164) on submit. India is the
// default everywhere, so every existing flow is unchanged. Non-Indian
// numbers are selectable but not yet functional end-to-end (the DB CHECK
// is India-only until the Phase 2 migration); the forms surface a clean
// "coming soon" for them.
//
// The national-number field still emits a plain digit string via onChange;
// the country is a separate channel via onCountryChange. Backend continues
// to receive 10-digit national for India.

import { COUNTRIES, type CountryCode } from '@jnati/phone'

interface Props {
  value: string
  onChange: (next: string) => void
  country: CountryCode
  onCountryChange: (next: CountryCode) => void
  disabled?: boolean
  placeholder?: string
  showLabel?: boolean
  autoComplete?: string
  topless?: boolean
  inputId?: string
}

export function PhoneInput({
  value, onChange, country, onCountryChange, disabled, placeholder,
  showLabel = true, autoComplete = 'tel', topless = false, inputId,
}: Props) {
  return (
    <div>
      {showLabel && (
        <label htmlFor={inputId} className={`block text-navy text-xs font-semibold mb-1 ${topless ? '' : 'mt-3'}`}>
          Phone Number
        </label>
      )}
      <div className="flex items-stretch gap-2">
        {/* #intl-auth-1c: real 7-country selector from @jnati/phone. India
            is the default (COUNTRIES[0]); flag + dial code shown. */}
        <div className="relative shrink-0">
          <select
            aria-label="Country"
            value={country}
            onChange={(e) => onCountryChange(e.target.value as CountryCode)}
            disabled={disabled}
            className="appearance-none border-2 border-border rounded-lg bg-chatBg text-navy text-sm font-semibold pl-3 pr-8 py-3 outline-none disabled:opacity-60 focus:border-navy"
          >
            {COUNTRIES.map((c) => (
              <option key={c.iso} value={c.iso}>{c.flag} {c.dialCode}</option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-navy/60 text-xs">▾</span>
        </div>
        <input
          id={inputId}
          type="tel"
          inputMode="numeric"
          autoComplete={autoComplete}
          placeholder={placeholder ?? '10-digit phone'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          // #web-phone-input-overflow: `min-w-0` is load-bearing — resets the
          // input's intrinsic flex min-width so it shrinks below ~175px on
          // narrow viewports instead of overflowing the auth card.
          className="flex-1 min-w-0 px-3 py-3 text-navy text-sm border-2 border-border rounded-lg outline-none disabled:opacity-60 disabled:bg-chatBg focus:border-navy"
        />
      </div>
    </div>
  )
}
